import { useId, useMemo, useState } from 'react'
import { ExternalLink, Search } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { DataTable, type Column } from '@/components/ui/DataTable'
import { Checkbox, Input, Select } from '@/components/ui/Field'
import { EmptyState } from '@/components/ui/States'
import { PAGE_TYPES } from '@/lib/constants'
import { pathOf, truncateMiddle } from '@/lib/format'
import type { CrawledPage, PageType } from '@/types'

/**
 * Filtering and sorting run client-side against the mock. Against the real
 * API these become query params — the page is paginated at 50 either way, so
 * the swap does not change the component's shape.
 */
export function PageInventoryTable({
  pages,
  loading,
  onRecrawl,
  onExclude,
  onImportance,
}: {
  pages: CrawledPage[]
  loading: boolean
  onRecrawl: (page: CrawledPage) => void
  onExclude: (page: CrawledPage) => void
  onImportance: (pageId: string, importance: number) => void
}) {
  const [query, setQuery] = useState('')
  const [type, setType] = useState<'all' | PageType>('all')
  const [changedOnly, setChangedOnly] = useState(false)
  const [minImportance, setMinImportance] = useState(0)
  const [editingImportance, setEditingImportance] = useState<string | null>(null)
  const changedOnlyId = useId()

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase()
    return pages.filter((p) => {
      if (p.error) return false // errors live in their own band
      if (type !== 'all' && p.pageType !== type) return false
      if (changedOnly && !p.changed) return false
      if (p.importance < minImportance) return false
      if (needle && !p.url.toLowerCase().includes(needle)) return false
      return true
    })
  }, [pages, query, type, changedOnly, minImportance])

  const columns: Column<CrawledPage>[] = [
    {
      key: 'url',
      header: 'URL',
      sortBy: (p) => p.url,
      render: (p) => (
        <span className="flex items-center gap-1.5">
          <span
            className={cnMuted(p.excluded)}
            title={p.url}
          >
            {truncateMiddle(pathOf(p.url), 46)}
          </span>
          {p.excluded && (
            <span className="rounded-[3px] bg-surface-sunk px-1.5 py-0.5 text-[10px] font-head font-semibold text-ink-muted">
              Excluded
            </span>
          )}
          <a
            href={p.url}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            aria-label={`Open ${p.url}`}
            className="text-ink-faint hover:text-brand-blue"
          >
            <ExternalLink size={11} aria-hidden />
          </a>
        </span>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      width: '120px',
      sortBy: (p) => p.pageType,
      render: (p) => <span className="capitalize text-ink-muted">{p.pageType}</span>,
    },
    {
      key: 'importance',
      header: 'Importance',
      width: '170px',
      align: 'right',
      sortBy: (p) => p.importance,
      render: (p) =>
        editingImportance === p.id ? (
          <Input
            autoFocus
            type="number"
            min={0}
            max={1}
            step={0.01}
            defaultValue={p.importance}
            aria-label={`Importance for ${p.url}`}
            className="h-7 w-20 text-right text-[12px]"
            onClick={(e) => e.stopPropagation()}
            onBlur={(e) => {
              onImportance(p.id, clamp01(Number(e.target.value)))
              setEditingImportance(null)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
              if (e.key === 'Escape') setEditingImportance(null)
            }}
          />
        ) : (
          // Inline override, logged server-side.
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setEditingImportance(p.id)
            }}
            className="inline-flex w-full items-center justify-end gap-2 rounded-[4px] px-1 py-0.5 hover:bg-surface-sunk"
            aria-label={`Edit importance for ${p.url}, currently ${p.importance.toFixed(2)}`}
          >
            <span className="h-1 w-20 overflow-hidden rounded-full bg-surface-sunk" aria-hidden>
              <span
                className="block h-full rounded-full bg-brand-blue"
                style={{ width: `${p.importance * 100}%` }}
              />
            </span>
            <span className="tabular text-[12px] text-ink-muted">{p.importance.toFixed(2)}</span>
          </button>
        ),
    },
    {
      key: 'changed',
      header: 'Changed',
      width: '80px',
      sortBy: (p) => (p.changed ? 1 : 0),
      render: (p) =>
        p.changed ? (
          <span className="flex items-center gap-1.5 text-[12px] text-warn">
            <span className="h-1.5 w-1.5 rounded-full bg-warn" aria-hidden />
            Yes
          </span>
        ) : (
          <span className="text-[12px] text-ink-faint">—</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      width: '150px',
      align: 'right',
      render: (p) => (
        <span className="flex justify-end gap-1.5">
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation()
              onRecrawl(p)
            }}
          >
            Re-crawl
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-brand-red-700"
            disabled={p.excluded}
            onClick={(e) => {
              e.stopPropagation()
              onExclude(p)
            }}
          >
            Exclude
          </Button>
        </span>
      ),
    },
  ]

  return (
    <Card className="overflow-hidden">
      <CardHeader
        title="Page inventory"
        description="Every page the last run reached. Excluding a page stops future extraction from it; it does not delete anything already extracted."
      />

      <div className="flex flex-wrap items-center gap-2 px-4 pt-3">
        <div className="relative">
          <Search
            size={13}
            aria-hidden
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint"
          />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="URL contains"
            aria-label="Filter by URL"
            className="h-8 w-56 pl-7 text-[13px]"
          />
        </div>

        <Select
          value={type}
          onChange={(e) => setType(e.target.value as 'all' | PageType)}
          aria-label="Filter by page type"
          className="h-8 w-40 text-[13px] capitalize"
        >
          <option value="all">All page types</option>
          {PAGE_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </Select>

        <Select
          value={String(minImportance)}
          onChange={(e) => setMinImportance(Number(e.target.value))}
          aria-label="Minimum importance"
          className="h-8 w-40 text-[13px]"
        >
          <option value="0">Any importance</option>
          <option value="0.5">0.50 and above</option>
          <option value="0.75">0.75 and above</option>
          <option value="0.9">0.90 and above</option>
        </Select>

        <label
          htmlFor={changedOnlyId}
          className="flex items-center gap-1.5 text-[13px] text-ink-muted"
        >
          <Checkbox
            id={changedOnlyId}
            checked={changedOnly}
            onChange={(e) => setChangedOnly(e.target.checked)}
          />
          Changed only
        </label>
      </div>

      <div className="pt-3">
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(p) => p.id}
          loading={loading}
          defaultSort={{ key: 'importance', dir: 'desc' }}
          rowClassName={(p) => (p.excluded ? 'opacity-55' : undefined)}
          empty={
            <EmptyState
              title="No pages match these filters"
              description="Widen the filters, or check the include patterns on the client profile."
            />
          }
        />
      </div>
    </Card>
  )
}

function clamp01(value: number): number {
  if (Number.isNaN(value)) return 0
  return Math.min(1, Math.max(0, value))
}

function cnMuted(excluded: boolean): string {
  return excluded
    ? 'font-mono text-[12px] text-ink-faint line-through'
    : 'font-mono text-[12px] text-ink-muted'
}
